<template>
  <div>
    <!-- Create / Edit Modal -->
    <b-modal id="listing-modal" :title="editId ? 'Edit Listing' : 'New Listing'" hide-footer size="md">
      <b-form @submit.prevent="saveListing">
        <b-form-group label="Bird Type">
          <b-form-select v-model="form.bird_type" :options="birdTypeOptions" required />
        </b-form-group>

        <!-- Pick the flock first — qty & weight auto-fill from it -->
        <b-form-group label="Link to Flock (optional)">
          <b-form-select v-model="form.flock_id" :options="flockOptions" @change="onFlockSelect" />
          <small class="text-muted">Pick a flock to auto-fill quantity &amp; weight. Delivery auto-records the sale and deducts birds.</small>
          <div v-if="estimating" class="est-hint text-muted">
            <b-spinner small class="mr-50" /> Estimating from flock…
          </div>
          <div v-else-if="flockEstimate" class="est-hint">
            <feather-icon icon="ZapIcon" size="14" class="text-primary mr-25" />
            Auto-filled: <strong>{{ flockEstimate.alive_birds.toLocaleString('en-IN') }}</strong> birds
            <span v-if="flockEstimate.avg_bird_weight"> × <strong>{{ flockEstimate.avg_bird_weight }} kg</strong> ≈ <strong>{{ Number(flockEstimate.est_qty_kg).toLocaleString('en-IN') }} kg</strong></span>
            <span v-else class="text-warning"> — no weight recorded yet, enter it manually</span>
          </div>
        </b-form-group>

        <b-form-group label="Total Qty (kg)">
          <b-form-input v-model.number="form.qty_kg_total" type="number" min="1" required :placeholder="form.flock_id ? 'auto-filled from flock' : ''" />
        </b-form-group>
        <b-form-group label="Avg bird weight (kg, optional)">
          <b-form-input v-model.number="form.avg_bird_weight" type="number" step="0.001" min="0.01" placeholder="auto-filled from flock if linked" />
        </b-form-group>
        <b-form-group label="Min Order (kg)">
          <b-form-input v-model.number="form.min_order_kg" type="number" min="1" required />
        </b-form-group>
        <b-form-group label="Price / kg (₹)">
          <b-form-input v-model.number="form.price_per_kg" type="number" step="0.01" min="0.01" required />
        </b-form-group>
        <b-form-group label="Pricing mode">
          <div class="pmode-row">
            <div
              class="pmode-opt"
              :class="{ active: form.price_type === 'fixed' }"
              @click="form.price_type = 'fixed'"
            >
              <div class="pmode-top">
                <feather-icon icon="TagIcon" size="16" />
                <span class="pmode-name">Fixed price</span>
                <feather-icon v-if="form.price_type === 'fixed'" icon="CheckCircleIcon" size="16" class="text-primary ml-auto" />
              </div>
              <small class="pmode-desc">No negotiation — lifters accept at your price.</small>
            </div>
            <div
              class="pmode-opt"
              :class="{ active: form.price_type === 'open' }"
              @click="form.price_type = 'open'"
            >
              <div class="pmode-top">
                <feather-icon icon="TrendingUpIcon" size="16" />
                <span class="pmode-name">Open for bids</span>
                <feather-icon v-if="form.price_type === 'open'" icon="CheckCircleIcon" size="16" class="text-primary ml-auto" />
              </div>
              <small class="pmode-desc">Lifters negotiate — you pick the winning bid.</small>
            </div>
          </div>
        </b-form-group>
        <b-form-group label="Valid Till (optional)">
          <b-form-input v-model="form.valid_till" type="date" />
        </b-form-group>
        <b-form-group label="Notes (optional)">
          <b-form-textarea v-model="form.notes" rows="2" />
        </b-form-group>
        <b-button type="submit" variant="primary" :disabled="saving" block>
          {{ saving ? 'Saving…' : (editId ? 'Update Listing' : 'Create Listing') }}
        </b-button>
      </b-form>
    </b-modal>

    <!-- Lifter bids on an open listing -->
    <b-modal id="bids-modal" title="Lifter bids" hide-footer size="md">
      <div v-if="bidsLoading" class="text-center p-3">Loading…</div>
      <div v-else-if="!bids.length" class="text-center text-muted p-3">No bids yet.</div>
      <b-table v-else :items="bids" :fields="[
        { key: 'integrator', label: 'Lifter' },
        { key: 'bid_price_per_kg', label: 'Bid ₹/kg' },
        { key: 'qty_kg', label: 'Qty (kg)' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: '' },
      ]" small responsive>
        <template #cell(integrator)="{ item }">{{ item.integrator ? item.integrator.name : '—' }}</template>
        <template #cell(bid_price_per_kg)="{ item }">₹{{ item.bid_price_per_kg }}</template>
        <template #cell(status)="{ item }"><b-badge :variant="item.status === 'accepted' ? 'success' : (item.status === 'closed' || item.status === 'rejected' ? 'secondary' : 'warning')">{{ item.status }}</b-badge></template>
        <template #cell(actions)="{ item }">
          <div v-if="item.status === 'pending'">
            <b-button size="sm" variant="success" class="mr-1" @click="acceptBid(item)">Accept</b-button>
            <b-button size="sm" variant="outline-danger" @click="rejectBid(item)">Reject</b-button>
          </div>
        </template>
      </b-table>
    </b-modal>

    <!-- ── Header ─────────────────────────────────────────────── -->
    <div class="ml-header">
      <div class="ml-header-text">
        <h3 class="ml-title">Bird Sales</h3>
        <p class="ml-sub">Post your birds for sale and let lifters bid.</p>
      </div>
      <b-button variant="primary" class="ml-sell-btn" @click="openCreate">
        <feather-icon icon="PlusIcon" size="16" class="mr-50" />
        Sell Birds
      </b-button>
    </div>

    <!-- ── Stat strip ─────────────────────────────────────────── -->
    <b-row class="ml-stats">
      <b-col cols="6" md="3">
        <div class="stat-tile">
          <span class="stat-val text-primary">{{ stats.active }}</span>
          <span class="stat-lbl">Active listings</span>
        </div>
      </b-col>
      <b-col cols="6" md="3">
        <div class="stat-tile">
          <span class="stat-val text-success">{{ stats.open }}</span>
          <span class="stat-lbl">Open for bids</span>
        </div>
      </b-col>
      <b-col cols="6" md="3">
        <div class="stat-tile">
          <span class="stat-val">{{ stats.totalKg.toLocaleString('en-IN') }}</span>
          <span class="stat-lbl">Kg listed</span>
        </div>
      </b-col>
      <b-col cols="6" md="3">
        <div class="stat-tile">
          <span class="stat-val text-warning">{{ stats.paused }}</span>
          <span class="stat-lbl">Paused</span>
        </div>
      </b-col>
    </b-row>

    <!-- ── Loading ────────────────────────────────────────────── -->
    <b-row v-if="loading">
      <b-col v-for="n in 3" :key="n" md="6" lg="4">
        <b-card><b-skeleton width="50%" /><b-skeleton width="70%" class="mt-1" /><b-skeleton width="40%" class="mt-2" /></b-card>
      </b-col>
    </b-row>

    <!-- ── Empty state ────────────────────────────────────────── -->
    <b-card v-else-if="!listings.length" class="ml-empty text-center">
      <b-img :src="require('@/assets/images/svg/infographics/no_data_infographic.svg')" class="ml-empty-img" />
      <h4 class="mt-2">No birds listed yet</h4>
      <p class="text-muted mb-2">Post your first sale — lifters will be notified and can bid.</p>
      <b-button variant="primary" @click="openCreate">
        <feather-icon icon="PlusIcon" size="16" class="mr-50" /> Sell Birds
      </b-button>
    </b-card>

    <!-- ── Listing cards ──────────────────────────────────────── -->
    <b-row v-else>
      <b-col v-for="item in listings" :key="item.id" md="6" lg="4">
        <div class="listing-card">
          <!-- top -->
          <div class="lc-top">
            <div>
              <span class="lc-bird">{{ (item.bird_type || '').toUpperCase() }}</span>
              <b-badge :variant="item.price_type === 'open' ? 'light-success' : 'light-primary'" class="lc-mode">
                {{ item.price_type === 'open' ? 'Open for bids' : 'Fixed price' }}
              </b-badge>
            </div>
            <b-badge :variant="statusPill(item.status)" pill class="lc-status">{{ prettyStatus(item.status) }}</b-badge>
          </div>

          <!-- price -->
          <div class="lc-price">
            <span class="lc-price-val">₹{{ item.price_per_kg }}</span>
            <span class="lc-price-unit">/ kg</span>
          </div>

          <!-- stock bar -->
          <div class="lc-stock-row">
            <span class="lc-stock-txt">{{ Number(item.qty_kg_available).toLocaleString('en-IN') }} of {{ Number(item.qty_kg_total).toLocaleString('en-IN') }} kg left</span>
            <span class="lc-stock-pct">{{ pctLeft(item) }}%</span>
          </div>
          <b-progress :value="Number(item.qty_kg_available)" :max="Number(item.qty_kg_total) || 1" height="6px" :variant="pctLeft(item) < 25 ? 'warning' : 'success'" class="mb-1" />

          <!-- meta -->
          <div class="lc-meta">
            <span>Min order {{ item.min_order_kg }} kg</span>
            <span v-if="item.valid_till">Valid till {{ fmtDate(item.valid_till) }}</span>
          </div>

          <div class="lc-divider" />

          <!-- actions -->
          <div class="lc-actions">
            <b-button
              v-if="item.price_type === 'open'"
              size="sm" variant="flat-primary" class="lc-act"
              @click="viewBids(item)"
            ><feather-icon icon="UsersIcon" size="14" /> Bids</b-button>
            <b-button size="sm" variant="flat-secondary" class="lc-act" @click="openEdit(item)">
              <feather-icon icon="Edit2Icon" size="14" /> Edit
            </b-button>
            <b-button v-if="item.status === 'active'" size="sm" variant="flat-warning" class="lc-act" @click="toggleStatus(item, 'paused')">
              <feather-icon icon="PauseIcon" size="14" /> Pause
            </b-button>
            <b-button v-if="item.status === 'paused'" size="sm" variant="flat-success" class="lc-act" @click="toggleStatus(item, 'active')">
              <feather-icon icon="PlayIcon" size="14" /> Resume
            </b-button>
            <b-button size="sm" variant="flat-danger" class="lc-act" @click="cancelListing(item)">
              <feather-icon icon="XIcon" size="14" /> Cancel
            </b-button>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import axios from '@/libs/axios'

export default {
  name: 'MarketListingList',
  data() {
    return {
      listings: [],
      loading: true,
      saving: false,
      editId: null,
      form: {
        bird_type: 'broiler',
        qty_kg_total: '',
        min_order_kg: 1,
        price_per_kg: '',
        price_type: 'fixed',
        valid_till: '',
        notes: '',
        flock_id: null,
        avg_bird_weight: null,
      },
      bidsListing: null,
      bids: [],
      bidsLoading: false,
      estimating: false,
      flockEstimate: null,
      flockOptions: [{ value: null, text: '— not linked —' }],
      birdTypeOptions: [
        { value: 'broiler', text: 'Broiler' },
        { value: 'layer',   text: 'Layer' },
        { value: 'breeder', text: 'Breeder' },
      ],
      fields: [
        { key: 'bird_type', label: 'Bird' },
        { key: 'price_per_kg', label: 'Price' },
        { key: 'stock', label: 'Stock' },
        { key: 'min_order_kg', label: 'Min (kg)' },
        { key: 'status', label: 'Status' },
        { key: 'valid_till', label: 'Valid Till' },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },
  computed: {
    stats() {
      const l = this.listings || []
      return {
        active: l.filter(x => x.status === 'active').length,
        paused: l.filter(x => x.status === 'paused').length,
        open: l.filter(x => x.price_type === 'open' && x.status === 'active').length,
        totalKg: l.reduce((s, x) => s + (Number(x.qty_kg_available) || 0), 0),
      }
    },
  },
  created() {
    this.fetchListings()
    this.fetchFlocks()
  },
  methods: {
    async onFlockSelect() {
      this.flockEstimate = null
      if (!this.form.flock_id) return
      this.estimating = true
      try {
        const { data } = await axios.get(`web/flock/${this.form.flock_id}/sale-estimate`)
        const est = data?.data ?? data
        this.flockEstimate = est
        // Auto-fill weight + total qty (owner can still edit before saving).
        if (est.avg_bird_weight) this.form.avg_bird_weight = est.avg_bird_weight
        if (est.est_qty_kg) this.form.qty_kg_total = est.est_qty_kg
      } catch (e) {
        this.$bvToast.toast(e?.response?.data?.message || 'Could not estimate from flock', { title: 'Estimate', variant: 'warning', solid: true })
      } finally {
        this.estimating = false
      }
    },
    pctLeft(item) {
      const total = Number(item.qty_kg_total) || 0
      if (!total) return 0
      return Math.round((Number(item.qty_kg_available) / total) * 100)
    },
    prettyStatus(s) {
      return { active: 'Active', paused: 'Paused', sold_out: 'Sold out', cancelled: 'Cancelled' }[s] || s
    },
    // valid_till comes back as a datetime (e.g. "2026-07-20 00:00:00" / ISO) — show date only.
    ymd(v) {
      if (!v) return ''
      return String(v).slice(0, 10)
    },
    fmtDate(v) {
      const d = this.ymd(v)
      if (!d) return ''
      const dt = new Date(`${d}T00:00:00`)
      if (Number.isNaN(dt.getTime())) return d
      return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    },
    statusPill(s) {
      return { active: 'light-success', paused: 'light-warning', sold_out: 'light-secondary', cancelled: 'light-danger' }[s] || 'light-secondary'
    },
    async fetchListings() {
      this.loading = true
      try {
        const { data } = await axios.get('web/market-listings')
        this.listings = data?.data ?? data ?? []
      } finally {
        this.loading = false
      }
    },
    async fetchFlocks() {
      try {
        const { data } = await axios.get('web/get-all-flock-list')
        const rows = data?.flock_list ?? data?.data ?? []
        const list = Array.isArray(rows) ? rows : (rows.data ?? [])
        this.flockOptions = [
          { value: null, text: '— not linked —' },
          ...list.map((f) => ({ value: f.flock_id, text: f.flock_id })),
        ]
      } catch (e) {
        // non-critical — linking a flock is optional
      }
    },
    openCreate() {
      this.editId = null
      this.flockEstimate = null
      this.form = { bird_type: 'broiler', qty_kg_total: '', min_order_kg: 1, price_per_kg: '', price_type: 'fixed', valid_till: '', notes: '', flock_id: null, avg_bird_weight: null }
      this.$bvModal.show('listing-modal')
    },
    openEdit(item) {
      this.editId = item.id
      this.flockEstimate = null
      this.form = {
        bird_type:    item.bird_type,
        qty_kg_total: item.qty_kg_total,
        min_order_kg: item.min_order_kg,
        price_per_kg: item.price_per_kg,
        price_type:   item.price_type ?? 'fixed',
        valid_till:   this.ymd(item.valid_till),
        notes:        item.notes ?? '',
        flock_id:     item.flock_id ?? null,
        avg_bird_weight: item.avg_bird_weight ?? null,
      }
      this.$bvModal.show('listing-modal')
    },
    async viewBids(item) {
      this.bidsListing = item
      this.bids = []
      this.bidsLoading = true
      this.$bvModal.show('bids-modal')
      try {
        const { data } = await axios.get(`market-listings/${item.id}/integrator-bids`)
        this.bids = data?.data?.bids ?? []
      } finally {
        this.bidsLoading = false
      }
    },
    async acceptBid(bid) {
      try {
        await axios.post(`market-integrator-bids/${bid.id}/accept`)
        this.$bvToast.toast('Bid accepted — listing closed, other bidders notified.', { title: 'Accepted', variant: 'success', solid: true })
        this.$bvModal.hide('bids-modal')
        this.fetchListings()
      } catch (e) {
        this.$bvToast.toast(e?.response?.data?.message ?? 'Failed to accept', { title: 'Error', variant: 'danger', solid: true })
      }
    },
    async rejectBid(bid) {
      try {
        await axios.post(`market-integrator-bids/${bid.id}/reject`)
        this.viewBids(this.bidsListing)
      } catch (e) {
        this.$bvToast.toast(e?.response?.data?.message ?? 'Failed to reject', { title: 'Error', variant: 'danger', solid: true })
      }
    },
    async saveListing() {
      // V13: validate min_order_kg <= qty_kg_total before sending
      if (this.form.min_order_kg > this.form.qty_kg_total) {
        this.$bvToast.toast('Minimum order quantity cannot exceed total quantity.', { title: 'Validation Error', variant: 'danger', solid: true })
        return
      }
      this.saving = true
      try {
        if (this.editId) {
          await axios.put(`web/market-listings/${this.editId}`, this.form)
        } else {
          await axios.post('web/market-listings', this.form)
        }
        this.$bvModal.hide('listing-modal')
        this.fetchListings()
      } catch (e) {
        const msg = e?.response?.data?.message ?? 'Failed to save listing'
        this.$bvToast.toast(msg, { title: 'Error', variant: 'danger', solid: true })
      } finally {
        this.saving = false
      }
    },
    async toggleStatus(item, status) {
      // V15: send only the status field, not the full item object
      try {
        await axios.put(`web/market-listings/${item.id}`, { status })
        this.fetchListings()
      } catch (e) {
        const msg = e?.response?.data?.message ?? 'Failed to update status'
        this.$bvToast.toast(msg, { title: 'Error', variant: 'danger', solid: true })
      }
    },
    async cancelListing(item) {
      const ok = await this.$bvModal.msgBoxConfirm('Cancel this listing? This cannot be undone.', { title: 'Confirm Cancel', okVariant: 'danger', okTitle: 'Yes, Cancel' })
      if (!ok) return
      try {
        await axios.delete(`web/market-listings/${item.id}`)
        this.fetchListings()
      } catch (e) {
        const msg = e?.response?.data?.message ?? 'Failed to cancel listing'
        this.$bvToast.toast(msg, { title: 'Error', variant: 'danger', solid: true })
      }
    },
    statusVariant(s) {
      return { active: 'success', paused: 'warning', sold_out: 'secondary', cancelled: 'danger' }[s] ?? 'secondary'
    },
  },
}
</script>

<style scoped>
.ml-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}
.ml-title { font-weight: 700; margin-bottom: 2px; }
.ml-sub { margin: 0; color: #6e6b7b; font-size: 0.9rem; }
.ml-sell-btn { font-weight: 600; box-shadow: 0 4px 12px rgba(41, 109, 180, 0.35); }

.ml-stats { margin-bottom: 0.5rem; }
.stat-tile {
  background: var(--white, #fff);
  border: 1px solid rgba(34, 41, 47, 0.08);
  border-radius: 0.6rem;
  padding: 0.9rem 1rem;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
}
.stat-val { font-size: 1.6rem; font-weight: 800; line-height: 1; font-variant-numeric: tabular-nums; }
.stat-lbl { font-size: 0.78rem; color: #6e6b7b; margin-top: 4px; }

.listing-card {
  background: var(--white, #fff);
  border: 1px solid rgba(34, 41, 47, 0.08);
  border-radius: 0.7rem;
  padding: 1.1rem 1.15rem;
  margin-bottom: 1.4rem;
  box-shadow: 0 2px 10px rgba(34, 41, 47, 0.05);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.listing-card:hover { box-shadow: 0 6px 20px rgba(34, 41, 47, 0.1); transform: translateY(-2px); }
.lc-top { display: flex; justify-content: space-between; align-items: flex-start; }
.lc-bird { font-size: 1.05rem; font-weight: 700; letter-spacing: 0.02em; }
.lc-mode { margin-left: 0.5rem; font-size: 0.68rem; }
.lc-status { font-size: 0.68rem; }
.lc-price { margin: 0.7rem 0 0.2rem; }
.lc-price-val { font-size: 1.9rem; font-weight: 800; color: #296DB4; font-variant-numeric: tabular-nums; }
.lc-price-unit { font-size: 0.9rem; color: #6e6b7b; margin-left: 3px; }
.lc-stock-row { display: flex; justify-content: space-between; font-size: 0.76rem; color: #6e6b7b; margin: 0.6rem 0 0.3rem; }
.lc-stock-pct { font-weight: 700; }
.lc-meta { display: flex; justify-content: space-between; font-size: 0.78rem; color: #6e6b7b; margin-top: 0.4rem; }
.lc-divider { height: 1px; background: rgba(34, 41, 47, 0.08); margin: 0.85rem 0 0.6rem; }
.lc-actions { display: flex; flex-wrap: wrap; gap: 0.15rem; }
.lc-act { font-size: 0.76rem; font-weight: 600; padding: 0.3rem 0.55rem; }

.ml-empty { padding: 2.5rem 1rem; }
.ml-empty-img { max-width: 180px; opacity: 0.9; }

/* Pricing-mode option cards */
.pmode-row { display: flex; gap: 0.6rem; }
.pmode-opt {
  flex: 1;
  border: 1.5px solid rgba(34, 41, 47, 0.12);
  border-radius: 0.5rem;
  padding: 0.7rem 0.75rem;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.pmode-opt:hover { border-color: rgba(41, 109, 180, 0.5); }
.pmode-opt.active { border-color: #296DB4; background: rgba(41, 109, 180, 0.06); }
.pmode-top { display: flex; align-items: center; gap: 0.4rem; font-weight: 600; }
.pmode-name { font-size: 0.86rem; }
.pmode-desc { display: block; color: #6e6b7b; font-size: 0.72rem; margin-top: 0.3rem; line-height: 1.3; }

.est-hint { font-size: 0.78rem; margin-top: 0.45rem; padding: 0.4rem 0.6rem; background: rgba(41, 109, 180, 0.06); border-radius: 0.35rem; }
</style>
